/* eslint-disable @typescript-eslint/naming-convention */
import classNames from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import { CMSCTA, CMSHTML } from '@swf/cms';
import { Size } from '@swf/core/enums';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import type { StickyFooter } from '@swf/generated/cms/types/StickyFooter';

import styles from './CMSStickyFooter.scss';

let fHeight: number;

const CMSStickyFooter: React.FC<StickyFooter> = ({
  html,
  cta,
  secondary_cta,
  display_desktop,
  display_mobile,
  background_color = 'black',
  scroll_percent = 10
}) => {
  const stickyFooterRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(display_desktop || display_mobile);
  const { greaterThan } = useScreenSize();
  const secondaryButton = secondary_cta?.title;

  useEffect(() => {
    const [footer] = document.getElementsByTagName('footer');

    const checkFooterHeight = () => {
      const stickyFooter = stickyFooterRef.current;

      if (!stickyFooter) {
        window.removeEventListener('resize', checkFooterHeight);
        return;
      }

      stickyFooter.style.height = 'auto';
      const footerStyle = window.getComputedStyle(stickyFooter);
      fHeight = parseInt(footerStyle.height);

      if (window.innerWidth <= 767) {
        fHeight = fHeight + 10;
      }

      stickyFooter.style.height = fHeight + 'px';
    };

    const checkFooterScroll = () => {
      const stickyFooter = stickyFooterRef.current;

      if (!stickyFooter) {
        return;
      }

      const scrollPercent = Math.round(
        (window.scrollY / (document.body.offsetHeight - window.innerHeight)) * 100
      );

      const footerBottom = footer.getBoundingClientRect().top;

      let hide = scrollPercent < (scroll_percent === null ? 10 : scroll_percent);

      if (footerBottom <= (window.innerHeight || document.documentElement.clientHeight)) {
        hide = true;
      }

      if (hide) {
        stickyFooter.style.bottom = '-' + fHeight + 'px';
      } else {
        stickyFooter.style.bottom = '0px';
      }
    };

    const checkFooterVisible = () => {
      if (greaterThan(Size.Small)) {
        setVisible(display_desktop);
      } else {
        setVisible(display_mobile);
      }
    };

    if (footer) {
      checkFooterHeight();
      checkFooterScroll();
      checkFooterVisible();

      window.addEventListener('resize', checkFooterHeight);
      window.addEventListener('resize', checkFooterVisible);
      window.addEventListener('scroll', checkFooterScroll);
    }

    return () => {
      window.removeEventListener('resize', checkFooterHeight);
      window.removeEventListener('resize', checkFooterVisible);
      window.removeEventListener('scroll', checkFooterScroll);
    };
  }, [display_desktop, display_mobile, greaterThan, scroll_percent]);

  return visible ? (
    <div
      className={classNames(styles.stickyFooter, styles[background_color || 'black'])}
      ref={stickyFooterRef}
    >
      <div
        className={classNames(styles.container, {
          [styles.ctaOnly]: !html.content,
          [styles.double]: secondaryButton
        })}
      >
        {html.content && (
          <div className={classNames({ [styles.withButtons]: secondaryButton })}>
            <CMSHTML {...html} />
          </div>
        )}
        <div
          className={classNames(html ? styles.right : styles.center, {
            [styles.doubleCta]: secondaryButton,
            [styles.buttonsOnly]: !html.content && secondaryButton
          })}
        >
          <CMSCTA {...cta} className={styles.cta} />
          {secondaryButton && <CMSCTA {...secondary_cta} className={styles.cta} />}
        </div>
      </div>
    </div>
  ) : null;
};

export default CMSStickyFooter;
